import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SCHEMA_VERSION } from '@os/doc';
import { copyText } from '../sharing/share-url';
import { Button, Dialog } from '../../ui';
import { DialogClose } from '../../ui/DialogClose';
import { CLIENT_BUILD, fetchHealth, formatVersion, type BuildInfo } from './version';

/** One line of a bug report: fixed English and ISO dates, whatever the interface language. */
const plain = (b: BuildInfo) => [`v${b.version}`, b.commit, b.builtAt].filter(Boolean).join(' · ');

/**
 * « About » (P5g, backlog §16.7): the web app's and the Worker's builds, the document schema, the browser and the
 * address, with one button that copies them for a bug report. Rendered open: the caller unmounts it in `onClose`.
 */
export function AboutDialog({
  onClose,
  check = fetchHealth,
  client = CLIENT_BUILD,
  copy = copyText,
}: {
  onClose: () => void;
  check?: () => Promise<BuildInfo | null>;
  client?: BuildInfo;
  copy?: (text: string) => Promise<boolean>;
}) {
  const { t, i18n } = useTranslation();
  // undefined while asking, null when no Worker answered.
  const [server, setServer] = useState<BuildInfo | null | undefined>(undefined);
  const [copied, setCopied] = useState<boolean | null>(null);
  useEffect(() => {
    let live = true;
    void check().then((s) => live && setServer(s));
    return () => {
      live = false;
    };
  }, [check]);

  const rows: [string, string][] = [
    [t('about.web'), formatVersion(client, i18n.language)],
    [t('about.server'), server === undefined ? t('about.checking') : server ? formatVersion(server, i18n.language) : t('about.unavailable')],
    [t('about.schema'), String(SCHEMA_VERSION)],
    [t('about.browser'), navigator.userAgent],
    [t('about.address'), location.origin],
  ];
  const report = [
    `Web: ${plain(client)}`,
    `Worker: ${server ? plain(server) : 'unavailable'}`,
    `Document schema: ${SCHEMA_VERSION}`,
    `Browser: ${navigator.userAgent}`,
    `Address: ${location.origin}`,
  ].join('\n');

  return (
    <Dialog
      title={t('about.title')}
      onClose={onClose}
      size="md"
      footer={
        <>
          <span role="status" className="mr-auto self-center text-xs text-fg-muted">
            {copied === null ? '' : copied ? t('about.copied') : t('about.copyFailed')}
          </span>
          <Button onClick={() => void copy(report).then(setCopied)}>{t('about.copy')}</Button>
          <DialogClose variant="primary">
            {t('about.close')}
          </DialogClose>
        </>
      }
    >
      <dl data-about="" className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-fg-muted">{label}</dt>
            <dd className="min-w-0 break-words font-mono text-xs leading-5 text-fg">{value}</dd>
          </div>
        ))}
      </dl>
    </Dialog>
  );
}
